import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { MetaDataService } from '../metadata.service';
import { NGXLogger } from 'ngx-logger';
import { Subject, takeUntil } from 'rxjs';
import { NgOptimizedImage } from '@angular/common';
import { TeaItemSummaryComponent } from '../tea-item-summary/tea-item-summary.component';
import { ImageModifierPipe } from '../image-modifer.pipe';
import { TeaByTypeResolverData } from './tea-by-type.resolver';

@Component({
    selector: 'tea-shop-fe-tea-by-type',
    templateUrl: './tea-by-type.component.html',
    styleUrls: ['./tea-by-type.component.css'],
    imports: [TeaItemSummaryComponent, NgOptimizedImage],
    providers: [ImageModifierPipe]
})
export class TeaByTypeComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private metaDataService = inject(MetaDataService);
  private logger = inject(NGXLogger);
  private imageModifier = inject(ImageModifierPipe);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  teaType: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  teas: any[] = [];
  errors: string[] = [];
  teaLadyAssetPath: string | null = null;
  optimizedTeaLadyImageUrl: string | null = null;
  imageLoading = false;
  private destroy$ = new Subject<void>();

  ngOnInit() {
    // Subscribe to route data changes instead of using snapshot
    this.route.data.pipe(
      takeUntil(this.destroy$)
    ).subscribe((data) => {
      this.updateComponentData(data);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async updateComponentData(data: Data) {
    // Reset component state
    this.teaType = null;
    this.teas = [];
    this.errors = [];
    this.teaLadyAssetPath = null;
    this.optimizedTeaLadyImageUrl = null;
    this.imageLoading = false;
    
    // Get the resolved data from the route
    const teaData: TeaByTypeResolverData = data['teaData'];
    
    if (teaData) {
      this.teaType = teaData.teaType;
      this.teas = teaData.teas;
      this.errors = teaData.errors;
      this.teaLadyAssetPath = teaData.teaLadyAssetPath;
      
      // Transform the tea lady image for NgOptimizedImage
      if (this.teaLadyAssetPath) {
        this.imageLoading = true;
        
        // Check if this is a local asset path (development)
        if (this.teaLadyAssetPath.startsWith('/assets/') || this.teaLadyAssetPath.startsWith('assets/')) {
          // For local assets, use the path directly without transformation
          this.optimizedTeaLadyImageUrl = this.teaLadyAssetPath;
        } else {
          // For remote URLs, use the transformation
          try {
            const transformedUrl = await this.imageModifier.transform(this.teaLadyAssetPath, { width: 600 });
            this.optimizedTeaLadyImageUrl = transformedUrl || null;
          } catch (error) {
            this.logger.error('Error transforming tea lady image URL', error);
            // Fallback to original path if transformation fails
            this.optimizedTeaLadyImageUrl = this.teaLadyAssetPath;
          }
        }
        
        this.imageLoading = false;
      }
      
      // Update metadata if tea type is available
      if (this.teaType) {
        this.metaDataService.updateMetadata({
          title: `${this.teaType.name} - Sencha Tea Bar`,
          image: this.teaLadyAssetPath || '',
        });
      }
    } else {
      this.errors.push('Failed to load tea data.');
    }
  }
}
